import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, BadgeCheck, HeartHandshake } from "lucide-react";

/**
 * AuthLayout — polished shell for every /auth/* page.
 *
 * Design goals (fintech vibes: Paystack / Kuda / PiggyVest):
 *   - Soft ambient gradient background with two subtle color blobs
 *   - Elevated rounded card with generous padding and clean spacing
 *   - Brand mark above the card (also acts as home link)
 *   - Consistent title/subtitle typography across every auth screen
 *   - Trust indicators row at the bottom (bank-grade / Paystack / Vonage)
 *   - Small "Need help?" footer link
 */
export default function AuthLayout({ title, subtitle, step, children, footer }) {
  return (
    <div
      className="relative min-h-screen overflow-hidden antialiased"
      style={{
        background:
          "radial-gradient(1200px 800px at 100% 0%, rgba(139,92,246,0.10), transparent 60%), " +
          "radial-gradient(900px 600px at 0% 100%, rgba(16,185,129,0.08), transparent 55%), " +
          "linear-gradient(180deg, #F8F7FF 0%, #FFFFFF 60%)",
      }}
    >
      {/* Decorative blobs (desktop only) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-24 hidden h-72 w-72 rounded-full bg-amber-300/30 blur-3xl lg:block"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-8 hidden h-80 w-80 rounded-full bg-amber-300/25 blur-3xl lg:block"
      />

      {/* Top-right step indicator (subtle) */}
      {step && (
        <div className="absolute right-6 top-6 hidden text-xs font-medium uppercase tracking-widest text-neutral-400 sm:block">
          {step}
        </div>
      )}

      <main className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-10 sm:px-6">
        {/* Brand mark */}
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 transition hover:opacity-80"
          data-testid="auth-brand"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#B45309] via-[#C2410C] to-[#9A3412] text-white ring-1 ring-white/40 shadow-md shadow-[#B45309]/20">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="text-lg font-semibold tracking-tight text-[#111827]">
            AfriTask <span style={{ color: "#B45309" }}>AI</span>
          </span>
        </Link>

        {/* Card */}
        <div
          className="w-full rounded-2xl border border-neutral-200/70 bg-white p-6 shadow-xl shadow-amber-900/5 sm:p-8"
          data-testid="auth-card"
        >
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-[26px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 text-sm text-neutral-500">{subtitle}</p>
          )}
          <div className="mt-6">{children}</div>

          {/* Trust indicators */}
          <div
            className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 border-t border-neutral-100 pt-4 text-[10px] text-neutral-500"
            data-testid="auth-trust-indicators"
          >
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-emerald-600" />
              Bank-grade security
            </span>
            <span className="inline-flex items-center gap-1">
              <BadgeCheck className="h-3 w-3 text-amber-700" />
              Powered by Paystack
            </span>
            <span className="inline-flex items-center gap-1">
              <HeartHandshake className="h-3 w-3 text-amber-700" />
              Vonage verified
            </span>
          </div>
        </div>

        {/* Optional per-page footer nav (e.g. "New here? Sign up") */}
        {footer && (
          <div className="mt-4 text-center text-xs text-neutral-500">
            {footer}
          </div>
        )}

        {/* Persistent help link */}
        <a
          href="mailto:hello@afritask.ai"
          className="mt-5 text-xs text-neutral-400 transition hover:text-amber-700"
          data-testid="auth-help-link"
        >
          Need help? Contact support
        </a>
      </main>
    </div>
  );
}
